import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { reviews, faqs } from '@/mocks/baoyu';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function AboutPage() {
  const location = useLocation();
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (location.hash === '#contact' || location.hash === '#reviews') {
      setTimeout(() => {
        const el = document.getElementById(location.hash.replace('#', ''));
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormStatus('idle');
    setFormError('');

    const formEl = e.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = formData.get('company_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      setSubmitting(false);
      return;
    }

    formData.delete('company_alt');

    try {
      const response = await fetch(
        'https://readdy.ai/api/form/d9u3jkn8l97n3eas3gmg',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
        }
      );

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse failure
      }

      if (response.ok && parsed && (parsed.code as string) === 'OK') {
        setFormStatus('success');
        formEl.reset();
      } else {
        const serverMsg =
          ((parsed as Record<string, unknown> | null)?.meta as Record<string, unknown> | null)
            ?.message as string
          || (parsed as Record<string, unknown> | null)?.message as string
          || responseText
          || '提交失败，请稍后再试';
        setFormStatus('error');
        setFormError(String(serverMsg));
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    } finally {
      setSubmitting(false);
    }
  };

  const breadcrumbItems = [{ label: '首页', path: '/' }, { label: '关于我们' }];

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于 baoyu',
    description: 'baoyu 品牌介绍、口碑评价与联系方式',
    url: 'https://bayo1u.github.io/about',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <main className="min-h-screen bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-20">
          <div className="mb-8">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          {/* Brand intro */}
          <section className="mb-20">
            <p className="text-sm text-primary-500 font-semibold tracking-wider mb-3">关于我们</p>
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-6">baoyu 品牌故事</h1>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  baoyu 诞生于对卓越品质的不懈追求。自品牌创立以来，我们始终坚持「以用户为中心」的理念，
                  将尖端科技与人性化设计完美融合，为全球用户打造真正有价值的产品。
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  我们的团队汇聚了来自世界各地的顶尖人才，他们在各自的领域拥有深厚的专业积累。
                  从硬件工程师到软件开发者，从工业设计师到用户体验专家，每一个 baoyu 人都在为同一个目标而努力——
                  创造能够真正改变人们生活的产品。
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  如今，baoyu 的产品已经走进了超过一千万用户的生活中，遍布全球 50 多个国家和地区。
                  但我们深知，这不仅是一个数字，更是一份沉甸甸的信任。未来，baoyu 将继续以创新为驱动，
                  以品质为基石，为每一位用户带来更多惊喜与价值。
                </p>
              </div>
              <div className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20creative%20workspace%20with%20warm%20ambient%20lighting%2C%20design%20team%20collaborating%2C%20cream%20and%20warm%20wood%20tones%2C%20clean%20aesthetic%20office%20environment%2C%20soft%20natural%20light%2C%20editorial%20corporate%20photography&width=800&height=600&seq=baoyu-about-brand&orientation=landscape"
                  alt="baoyu 品牌团队"
                  title="baoyu 品牌团队"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </section>

          {/* Reviews */}
          <section id="reviews" className="mb-20 scroll-mt-20">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-2">用户口碑</h2>
            <p className="text-sm text-foreground-600 mb-8">来自 baoyu 用户的真实评价</p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviews.map((review) => (
                <div
                  key={review.id}
                  className="bg-background-100 rounded-2xl p-6 border border-background-200/70"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={review.avatar}
                      alt={review.name}
                      className="w-11 h-11 rounded-full object-cover"
                    />
                    <div>
                      <div className="text-sm font-semibold text-foreground-950">{review.name}</div>
                      <div className="text-xs text-foreground-600">{review.role}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-xs ${
                          i < review.rating
                            ? 'ri-star-fill text-amber-400'
                            : 'ri-star-line text-foreground-400'
                        }`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed">{review.content}</p>
                  <p className="mt-4 text-xs text-foreground-500">{review.date}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section id="contact" className="scroll-mt-20">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-2">联系我们</h2>
            <p className="text-sm text-foreground-600 mb-8">
              有任何问题或合作意向？请填写下方表单，baoyu 团队将尽快与你取得联系
            </p>

            <div className="max-w-2xl">
              <form
                data-readdy-form
                id="contact-form"
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-red-400">*</span>
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200"
                      placeholder="请输入你的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-red-400">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200"
                      placeholder="请输入你的邮箱"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    id="subject"
                    name="subject"
                    type="text"
                    className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200"
                    placeholder="请输入主题"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言 <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200 resize-none"
                    placeholder="请输入你想说的话（限500字）"
                  ></textarea>
                </div>

                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />

                <button
                  type="submit"
                  disabled={submitting}
                  className="px-8 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold transition-all duration-200 whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  {submitting ? '提交中...' : '发送留言'}
                </button>

                {formStatus === 'success' && (
                  <p className="text-sm text-accent-600">感谢你的留言！baoyu 团队会尽快与你联系。</p>
                )}
                {formStatus === 'error' && (
                  <p className="text-sm text-red-500">{formError || '提交失败，请稍后再试'}</p>
                )}
              </form>
            </div>
          </section>
        </div>
      </main>
    </>
  );
}